import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getSubscriptionPlans, createRazorpayOrder, verifyRazorpayPayment } from '../../api';
import { loadRazorpayScript, formatRazorpayVerifyError } from '../../utils/courseCheckout';
import { Check, Zap, Star, Shield, CreditCard, Sparkles, Clock, ArrowRight, X } from 'lucide-react';
import PageLoader from '../../components/PageLoader';

const getCycle = (plan) => {
  const cycle = String(plan?.billingCycle ?? plan?.planType ?? '').toLowerCase();
  if (cycle === 'annually' || cycle === 'annual' || cycle === 'yearly' || cycle === 'year') return 'annually';
  if (cycle === 'monthly' || cycle === 'month') return 'monthly';
  const priceText = String(plan?.price ?? '').toLowerCase();
  if (priceText.includes('/year') || priceText.includes('/ year') || priceText.includes('annual')) return 'annually';
  return 'monthly';
};

const parseNumericPrice = (value) => {
  const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatAmount = (value) => {
  const n = parseNumericPrice(value);
  return `₹${n.toLocaleString('en-IN')}`;
};

const getPeriodLabel = (cycle) => (cycle === 'annually' ? '/year' : '/month');

const sortPlans = (items) => {
  const list = [...items];
  list.sort((a, b) => {
    const aIsFree = parseNumericPrice(a.price) === 0 || String(a.id).toLowerCase() === 'free';
    const bIsFree = parseNumericPrice(b.price) === 0 || String(b.id).toLowerCase() === 'free';
    if (aIsFree && !bIsFree) return -1;
    if (!aIsFree && bIsFree) return 1;
    return parseNumericPrice(a.price) - parseNumericPrice(b.price);
  });
  return list;
};

const UserSubscription = () => {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [payingPlanId, setPayingPlanId] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await getSubscriptionPlans();
        const normalized = Array.isArray(list) ? list : [];
        setPlans(normalized);
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || 'Failed to load subscription plans');
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const freePlan = useMemo(
    () => plans.find((p) => parseNumericPrice(p.price) === 0 || String(p.id).toLowerCase() === 'free') || null,
    [plans]
  );

  const currentPlanId = useMemo(() => {
    const actualId = user?.subscriptionPlan;
    if (!actualId) return freePlan?.id || '';
    const actualPlan = plans.find(p => String(p._id) === String(actualId) || String(p.id) === String(actualId));
    if (actualPlan && getCycle(actualPlan) === billingCycle) {
      return actualPlan.id;
    }
    if (actualPlan && parseNumericPrice(actualPlan.price) > 0) {
      return null;
    }
    return freePlan?.id || '';
  }, [user?.subscriptionPlan, plans, billingCycle, freePlan]);

  const currentPlan = useMemo(() => {
    const actualId = user?.subscriptionPlan;
    if (!actualId || actualId === 'free') return freePlan;
    return plans.find(p => String(p._id) === String(actualId) || String(p.id) === String(actualId)) || freePlan;
  }, [user?.subscriptionPlan, plans, freePlan]);

  const visiblePlans = useMemo(() => {
    if (!plans.length) return [];
    const filtered = plans.filter((p) => {
      const isFree = parseNumericPrice(p.price) === 0 || String(p.id).toLowerCase() === 'free';
      return getCycle(p) === billingCycle || isFree;
    });
    return sortPlans(filtered.length ? filtered : plans);
  }, [plans, billingCycle]);

  const recommendedPlanId = useMemo(() => {
    if (!visiblePlans.length) return null;
    const paid = visiblePlans.filter((p) => parseNumericPrice(p.price) > 0 && p.id !== currentPlanId);
    if (!paid.length) return null;
    const highest = [...paid].sort((a, b) => parseNumericPrice(b.price) - parseNumericPrice(a.price))[0];
    return highest.id;
  }, [visiblePlans, currentPlanId]);

  const handleSelectPlan = async (plan) => {
    if (!user || !plan?._id) return;
    try {
      setError('');
      setPayingPlanId(plan.id);

      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) {
        setError('Unable to load Razorpay. Please check your internet and try again.');
        return;
      }

      const order = await createRazorpayOrder(plan._id);
      if (!order?.orderId || !order?.keyId) {
        setError('Unable to create payment order. Please try again.');
        return;
      }

      const rz = new window.Razorpay({
        key: order.keyId,
        amount: Number(order.amount),
        currency: order.currency || 'INR',
        name: 'Market Mavericks',
        description: `${plan.name} (${getCycle(plan) === 'annually' ? 'Annual' : 'Monthly'})`,
        order_id: order.orderId,
        prefill: {
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          email: user.email || '',
          contact: user.mobile || user.number || '',
        },
        theme: { color: '#10b981' },
        handler: async function (response) {
          try {
            setPayingPlanId('verifying');
            await verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            await refreshUser();
            setSuccess('Subscription updated successfully!');
            setTimeout(() => setSuccess(''), 5000);
          } catch (verifyErr) {
            setError(formatRazorpayVerifyError(verifyErr));
          } finally {
            setPayingPlanId('');
          }
        },
        modal: {
          ondismiss: () => {
            setPayingPlanId('');
          },
        },
      });

      rz.on('payment.failed', (resp) => {
        const e = resp?.error || {};
        setError(
          [e.description, e.reason, e.step].filter(Boolean).join(' — ') ||
            'Payment failed. In test mode use Razorpay test cards only.'
        );
        setPayingPlanId('');
      });

      rz.open();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to start payment');
      setPayingPlanId('');
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-6xl 4xl:max-w-10xl mx-auto px-4 pb-20">
      {/* 1. Normal Header */}
      <div className="text-center py-4 space-y-4">
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">Simple, Transparent Pricing</h1>
        <p className="text-gray-500 text-lg max-w-2xl mx-auto">
          Choose a plan that works for you. Upgrade or downgrade anytime.
        </p>
      </div>

      {/* 2. Toggle Section */}
      <div className="flex flex-col items-center gap-6 mb-3">
        <div className="inline-flex items-center p-1 bg-gray-100 rounded-2xl border border-gray-200">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2.5 text-sm font-bold rounded-xl transition-all ${
              billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annually')}
            className={`px-6 py-2.5 text-sm font-bold rounded-xl transition-all flex items-center gap-2 ${
              billingCycle === 'annually' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Annually
          </button>
        </div>
      </div>

     
     

      {/* 4. Notifications */}
      <div className="max-w-2xl mx-auto mb-8 space-y-3">
        {success && (
          <div className="p-4 rounded-2xl bg-emerald-100 border border-emerald-200 text-emerald-800 flex items-center gap-3 animate-in fade-in duration-300">
            <Check size={20} className="shrink-0" />
            <span className="font-bold text-sm">{success}</span>
          </div>
        )}
        {error && (
          <div className="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-700 flex items-center gap-3">
            <X size={20} className="shrink-0" />
            <span className="font-bold text-sm">{error}</span>
          </div>
        )}
      </div>

      {/* 5. Modern Cards Grid */}
      {loading ? (
        <PageLoader />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-6">
          {visiblePlans.map((plan) => {
            const isActive = plan.id === currentPlanId;
            const priceNumber = parseNumericPrice(plan.price);
            const isFree = priceNumber === 0;
            const isPaidUser = currentPlan && parseNumericPrice(currentPlan.price) > 0;
            const features = Array.isArray(plan.features) ? plan.features : [];
            const userHasAllAccess = currentPlan?.isAllAccess;

            return (
              <div
                key={plan.id}
                className={`flex flex-col rounded-[2rem] bg-white border-2 p-8 transition-all hover:shadow-xl ${
                  isActive 
                    ? 'border-emerald-500 ring-4 ring-emerald-50 shadow-md scale-[1.02]' 
                    : 'border-gray-100 hover:border-emerald-200'
                }`}
              >
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-black text-gray-900">{plan.name}</h3>
                    {isActive && (
                      <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full border border-emerald-200">Current</span>
                    )}
                  </div>
                  {plan.isAllAccess && (
                    <div className="mt-2 flex">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-black uppercase tracking-widest border border-amber-200">
                        <Sparkles size={12} />
                        All Access Enabled
                      </span>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1 font-bold">{plan.tag || 'Standard Access'}</p>
                </div>

                <div className="mb-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black text-gray-900">{isFree ? '₹0' : formatAmount(plan.price)}</span>
                    {!isFree && (
                      <span className="text-gray-400 font-bold text-sm">{getPeriodLabel(getCycle(plan))}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-2 leading-relaxed">{plan.description || "Everything you need to master the markets."}</p>
                </div>

                <div className="space-y-4 mb-8 flex-1">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Included Features</p>
                  <ul className="space-y-3">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check size={16} className="text-emerald-500 shrink-0 mt-0.5" strokeWidth={3} />
                        <span className="text-sm font-medium text-gray-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={isActive || (isFree && isPaidUser) || (payingPlanId === plan.id || payingPlanId === 'verifying') || (userHasAllAccess && !isActive)}
                  className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all ${
                    isActive
                      ? 'bg-gray-100 text-gray-400 cursor-default'
                      : (isFree && isPaidUser) || (userHasAllAccess && !isActive)
                        ? 'bg-gray-50 text-gray-300 cursor-not-allowed border border-gray-100'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-100'
                  }`}
                >
                  {isActive ? (
                    'Your Active Plan'
                  ) : (isFree && isPaidUser) ? (
                    'Not Available'
                  ) : (payingPlanId === plan.id || payingPlanId === 'verifying') ? (
                    'Processing...'
                  ) : (
                    'Upgrade Now'
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

import { Link } from 'react-router-dom';

export default UserSubscription;

